import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Header from "./Header";
import Footer from "./Footer";
import { SITE } from "../../lib/data";

// Effective date is set once at build/deploy time so the page shows a stable date.
const EFFECTIVE_DATE = new Date().toLocaleDateString("en-IN", {
  day: "numeric",
  month: "long",
  year: "numeric",
});

const SECTIONS = [
  {
    id: "introduction",
    number: "01",
    title: "Introduction",
    body: (
      <>
        Amarja Hills values the privacy of every visitor to our website and is
        committed to protecting the personal information you choose to share
        with us. This policy explains what we collect, how we use it, and the
        care we take to keep it safe.
      </>
    ),
  },
  {
    id: "information-we-collect",
    number: "02",
    title: "Information We Collect",
    body: (
      <>
        <p>
          When you interact with our website — through enquiry forms, contact
          forms, or lead generation forms — we may collect the following:
        </p>
        <ul className="mt-4 space-y-2 list-none">
          {[
            "Name",
            "Phone Number",
            "Email Address",
            "City",
            "Budget Range",
            "Property Preferences",
            "Any additional information you voluntarily submit through our forms",
          ].map((item) => (
            <li key={item} className="flex gap-3 items-baseline">
              <span className="text-[#987F53] mt-0.5">·</span>
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </>
    ),
  },
  {
    id: "how-we-use",
    number: "03",
    title: "How We Use Your Information",
    body: (
      <>
        <p>The information you share may be used to:</p>
        <ul className="mt-4 space-y-2 list-none">
          {[
            "Respond to your enquiries in a timely and personal manner",
            "Schedule private site visits at your convenience",
            "Share project information, brochures, and pricing",
            "Contact you via phone, email, SMS, or WhatsApp regarding your enquiry",
            "Improve our customer service and website experience",
            "Send you relevant project updates — you may opt out at any time",
          ].map((item) => (
            <li key={item} className="flex gap-3 items-baseline">
              <span className="text-[#987F53] mt-0.5">·</span>
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </>
    ),
  },
  {
    id: "data-protection",
    number: "04",
    title: "Data Protection",
    body: (
      <>
        Amarja Hills takes reasonable technical and organisational measures to
        protect your personal information against unauthorised access, misuse,
        alteration, or disclosure. Access is limited to authorised personnel
        who need the information to serve you.
      </>
    ),
  },
  {
    id: "sharing-information",
    number: "05",
    title: "Sharing Information",
    body: (
      <>
        <p className="font-medium text-[#1A362D]">
          We do not sell or rent personal information to third parties.
        </p>
        <p className="mt-4">
          Information may only be shared with trusted service providers who
          assist Amarja Hills in operating its business — such as CRM,
          hosting, or communication platforms — or when disclosure is required
          by law.
        </p>
      </>
    ),
  },
  {
    id: "cookies",
    number: "06",
    title: "Cookies",
    body: (
      <>
        Our website may use cookies and analytics tools to improve your
        browsing experience and help us understand how the site is used. You
        can control or disable cookies through your browser settings at any
        time.
      </>
    ),
  },
  {
    id: "third-party-services",
    number: "07",
    title: "Third-Party Services",
    body: (
      <>
        <p>
          Our website may use the following third-party services to analyse
          traffic and reach the right audience:
        </p>
        <ul className="mt-4 space-y-2 list-none">
          {[
            "Google Analytics",
            "Google Ads",
            "LinkedIn Ads",
            "Meta Pixel (where applicable)",
          ].map((item) => (
            <li key={item} className="flex gap-3 items-baseline">
              <span className="text-[#987F53] mt-0.5">·</span>
              <span>{item}</span>
            </li>
          ))}
        </ul>
        <p className="mt-4">
          These services may collect anonymous browsing information in
          accordance with their own privacy policies.
        </p>
      </>
    ),
  },
  {
    id: "your-rights",
    number: "08",
    title: "Your Rights",
    body: (
      <>
        <p>As a visitor, you have the right to:</p>
        <ul className="mt-4 space-y-2 list-none">
          {[
            "Access the personal information we hold about you",
            "Correct information that is inaccurate or incomplete",
            "Request deletion of your personal information",
            "Withdraw consent for future marketing communications",
          ].map((item) => (
            <li key={item} className="flex gap-3 items-baseline">
              <span className="text-[#987F53] mt-0.5">·</span>
              <span>{item}</span>
            </li>
          ))}
        </ul>
        <p className="mt-4">
          To exercise any of these rights, please write to us using the
          contact details below.
        </p>
      </>
    ),
  },
];

export default function PrivacyPolicy() {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = "Privacy Policy · Amarja Hills";
    window.scrollTo({ top: 0, behavior: "instant" });

    // Ensure the page is indexable for LinkedIn Lead Gen requirements etc.
    let robots = document.querySelector('meta[name="robots"]');
    const hadRobots = !!robots;
    const previousRobots = robots?.getAttribute("content") || null;
    if (!robots) {
      robots = document.createElement("meta");
      robots.setAttribute("name", "robots");
      document.head.appendChild(robots);
    }
    robots.setAttribute("content", "index,follow");

    return () => {
      document.title = prevTitle;
      if (!hadRobots) {
        robots?.parentNode?.removeChild(robots);
      } else if (previousRobots !== null) {
        robots.setAttribute("content", previousRobots);
      }
    };
  }, []);

  return (
    <div
      data-testid="privacy-policy-page"
      className="bg-[#F7F5F0] text-[#1C1C1C] min-h-screen flex flex-col"
    >
      <Header />

      <main className="flex-1">
        {/* Subtle hero */}
        <section
          data-testid="privacy-hero"
          className="relative overflow-hidden border-b border-[#1A362D]/10"
        >
          <div className="absolute inset-0 pointer-events-none">
            <img
              src="/img/site/drone3.jpg"
              alt=""
              className="absolute inset-0 w-full h-full object-cover opacity-20"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-[#F7F5F0]/80 via-[#F7F5F0]/90 to-[#F7F5F0]" />
          </div>

          <div className="relative max-w-[80rem] mx-auto px-6 sm:px-10 lg:px-16 pt-28 pb-20 sm:pt-36 sm:pb-24">
            <p
              data-testid="privacy-eyebrow"
              className="text-[11px] tracking-[0.32em] uppercase text-[#987F53] font-semibold mb-6"
            >
              Amarja Hills · Legal
            </p>
            <h1
              data-testid="privacy-title"
              className="font-serif-display text-5xl sm:text-6xl md:text-7xl text-[#1A362D] leading-[0.98] tracking-tight font-light"
            >
              Privacy Policy
            </h1>
            <p
              data-testid="privacy-subtitle"
              className="mt-6 max-w-2xl text-lg sm:text-xl text-[#3A453B] leading-relaxed font-light"
            >
              Your privacy matters to us. This policy explains how Amarja
              Hills collects, uses, and protects your information.
            </p>
            <p
              data-testid="privacy-effective-date"
              className="mt-8 text-[11px] tracking-[0.28em] uppercase text-[#4A524D] font-semibold"
            >
              Effective Date · {EFFECTIVE_DATE}
            </p>
          </div>
        </section>

        {/* Body */}
        <section className="max-w-[80rem] mx-auto px-6 sm:px-10 lg:px-16 py-20 sm:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
            {/* Sticky ToC on desktop */}
            <aside className="hidden lg:block lg:col-span-4">
              <div className="sticky top-28">
                <p className="text-[11px] tracking-[0.28em] uppercase text-[#987F53] font-semibold mb-6">
                  Contents
                </p>
                <ol className="space-y-3 text-sm">
                  {SECTIONS.map((s) => (
                    <li key={s.id}>
                      <a
                        href={`#${s.id}`}
                        className="flex gap-4 text-[#3A453B] hover:text-[#1A362D] transition-colors"
                      >
                        <span className="font-serif-display text-base italic text-[#987F53] font-light w-6 flex-shrink-0">
                          {s.number}
                        </span>
                        <span>{s.title}</span>
                      </a>
                    </li>
                  ))}
                  <li>
                    <a
                      href="#contact"
                      className="flex gap-4 text-[#3A453B] hover:text-[#1A362D] transition-colors"
                    >
                      <span className="font-serif-display text-base italic text-[#987F53] font-light w-6 flex-shrink-0">
                        09
                      </span>
                      <span>Contact</span>
                    </a>
                  </li>
                </ol>
              </div>
            </aside>

            {/* Content */}
            <article className="lg:col-span-8 space-y-16 text-[#3A453B] leading-relaxed">
              {SECTIONS.map((s) => (
                <section
                  key={s.id}
                  id={s.id}
                  data-testid={`privacy-section-${s.id}`}
                  className="scroll-mt-24"
                >
                  <div className="flex items-baseline gap-5 mb-6">
                    <span className="font-serif-display text-3xl italic text-[#987F53] font-light leading-none flex-shrink-0">
                      {s.number}
                    </span>
                    <h2 className="font-serif-display text-3xl sm:text-4xl text-[#1A362D] tracking-tight font-light leading-tight">
                      {s.title}
                    </h2>
                  </div>
                  <div className="text-base sm:text-lg font-light leading-[1.75] space-y-4 pl-0 sm:pl-14">
                    {s.body}
                  </div>
                </section>
              ))}

              {/* Contact */}
              <section
                id="contact"
                data-testid="privacy-section-contact"
                className="scroll-mt-24"
              >
                <div className="flex items-baseline gap-5 mb-6">
                  <span className="font-serif-display text-3xl italic text-[#987F53] font-light leading-none flex-shrink-0">
                    09
                  </span>
                  <h2 className="font-serif-display text-3xl sm:text-4xl text-[#1A362D] tracking-tight font-light leading-tight">
                    Contact
                  </h2>
                </div>
                <div className="pl-0 sm:pl-14 text-base sm:text-lg font-light leading-[1.75]">
                  <p className="mb-6">
                    For any privacy-related questions, requests, or concerns,
                    please reach out to us:
                  </p>
                  <div className="bg-white border border-[#1A362D]/10 p-6 sm:p-8 space-y-3">
                    <p className="font-serif-display text-2xl text-[#1A362D] font-light">
                      Amarja Hills
                    </p>
                    <p>
                      <span className="text-[11px] tracking-[0.24em] uppercase text-[#4A524D] font-semibold mr-3">
                        Website
                      </span>
                      <a
                        data-testid="privacy-contact-website"
                        href="https://www.amarjahills.com"
                        className="text-[#1A362D] hover:text-[#987F53] transition-colors underline underline-offset-4 decoration-[#1A362D]/20 hover:decoration-[#987F53]"
                      >
                        www.amarjahills.com
                      </a>
                    </p>
                    <p>
                      <span className="text-[11px] tracking-[0.24em] uppercase text-[#4A524D] font-semibold mr-3">
                        Email
                      </span>
                      <a
                        data-testid="privacy-contact-email"
                        href={`mailto:${SITE.email}`}
                        className="text-[#1A362D] hover:text-[#987F53] transition-colors underline underline-offset-4 decoration-[#1A362D]/20 hover:decoration-[#987F53]"
                      >
                        {SITE.email}
                      </a>
                    </p>
                    <p>
                      <span className="text-[11px] tracking-[0.24em] uppercase text-[#4A524D] font-semibold mr-3">
                        Phone
                      </span>
                      <a
                        data-testid="privacy-contact-phone"
                        href={`tel:${SITE.telHref}`}
                        className="text-[#1A362D] hover:text-[#987F53] transition-colors underline underline-offset-4 decoration-[#1A362D]/20 hover:decoration-[#987F53]"
                      >
                        {SITE.phone}
                      </a>
                    </p>
                  </div>
                </div>
              </section>

              {/* Back link */}
              <div className="pt-8 border-t border-[#1A362D]/10">
                <Link
                  to="/"
                  data-testid="privacy-back-home"
                  className="inline-flex items-center gap-2 text-[11px] tracking-[0.24em] uppercase font-semibold text-[#1A362D] hover:text-[#987F53] transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to Amarja Hills
                </Link>
              </div>
            </article>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
